/**
 * Ticket inspector layout prefs.
 *
 * Width and full-page live next to the interface scale and the chime toggle: they are a
 * property of this browser on this monitor, not an account setting and not a table. A new
 * SQLite row would follow the operator across machines and then fight the display they are
 * actually looking at.
 */

import { uiScale } from './display.ts';

export const INSPECTOR_WIDTH_KEY = 'nightshift.inspector-width';
export const INSPECTOR_FULL_KEY = 'nightshift.inspector-full';

/** Matches the drawer's original `min(820px, 92vw)` default. */
export const DEFAULT_INSPECTOR_WIDTH = 820;
export const MIN_INSPECTOR_WIDTH = 420;
export const MAX_INSPECTOR_WIDTH = 1400;
/** Arrow keys move the left edge by this many CSS pixels. */
export const INSPECTOR_WIDTH_STEP = 24;
/** Below this, the inspector fills the viewport; the drag handle would only fight the board. */
export const INSPECTOR_NARROW_MQ = '(max-width: 900px)';

export interface InspectorPrefsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function fallbackStore(): InspectorPrefsStore | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Clamp a remembered width so a huge or tiny stored value cannot hide the board or the
 * inspector. `viewportWidth` leaves a sliver of the underlying view on the left when the
 * display is narrower than the absolute max.
 */
export function clampInspectorWidth(px: number, viewportWidth = Number.POSITIVE_INFINITY): number {
  if (!Number.isFinite(px)) return DEFAULT_INSPECTOR_WIDTH;
  const room = Number.isFinite(viewportWidth)
    ? Math.max(MIN_INSPECTOR_WIDTH, viewportWidth - 48)
    : MAX_INSPECTOR_WIDTH;
  const max = Math.min(MAX_INSPECTOR_WIDTH, room);
  return Math.round(Math.min(max, Math.max(MIN_INSPECTOR_WIDTH, px)));
}

/**
 * Width the docked inspector can actually use, in the units its own `width` is set in.
 *
 * The shell applies the interface scale as `zoom` on the document root. A `position: fixed`
 * panel is still positioned against the window but its geometry is painted through that zoom,
 * so `width: 1232px` at the default 135% paints 1663px and hangs off both sides of a 1280px
 * window. The ruler is therefore the window divided by the scale, not the window.
 */
export function inspectorViewportWidth(): number {
  if (typeof window === 'undefined') return Number.POSITIVE_INFINITY;
  return window.innerWidth / (uiScale() / 100);
}

/** A pointer x from a mouse event, in the same scaled units as `inspectorViewportWidth`. */
export function inspectorPointerX(clientX: number): number {
  return clientX / (uiScale() / 100);
}

/**
 * The remembered width, clamped to the display it is about to be used on.
 *
 * The clamp needs the viewport: a 1400px width remembered from a wide monitor would otherwise
 * open a drawer wider than a 1000px window, with the resize handle off-screen.
 */
export function inspectorWidth(
  store: InspectorPrefsStore | null = fallbackStore(),
  viewportWidth: number = inspectorViewportWidth(),
): number {
  if (!store) return clampInspectorWidth(DEFAULT_INSPECTOR_WIDTH, viewportWidth);
  try {
    const raw = Number(store.getItem(INSPECTOR_WIDTH_KEY));
    if (Number.isFinite(raw) && raw > 0) return clampInspectorWidth(raw, viewportWidth);
  } catch {
    // Storage unavailable or unreadable; the default applies.
  }
  return clampInspectorWidth(DEFAULT_INSPECTOR_WIDTH, viewportWidth);
}

export function setInspectorWidth(
  px: number,
  store: InspectorPrefsStore | null = fallbackStore(),
  viewportWidth = Number.POSITIVE_INFINITY,
): number {
  const next = clampInspectorWidth(px, viewportWidth);
  if (store) {
    try {
      store.setItem(INSPECTOR_WIDTH_KEY, String(next));
    } catch {
      // The change still applies for this page lifetime.
    }
  }
  return next;
}

export function inspectorFullPage(store: InspectorPrefsStore | null = fallbackStore()): boolean {
  if (!store) return false;
  try {
    return store.getItem(INSPECTOR_FULL_KEY) === '1';
  } catch {
    return false;
  }
}

export function setInspectorFullPage(
  on: boolean,
  store: InspectorPrefsStore | null = fallbackStore(),
): boolean {
  if (store) {
    try {
      store.setItem(INSPECTOR_FULL_KEY, on ? '1' : '0');
    } catch {
      // Same as width: the session still sees the mode.
    }
  }
  return on;
}

/** Side panel (resizable) vs filling the viewport. Narrow screens are always full. */
export function inspectorFillsViewport(fullPage: boolean, narrow: boolean): boolean {
  return fullPage || narrow;
}

/**
 * Right-docked panel: the handle is the left edge, so a pointer further left is a wider
 * inspector. The board stays in view to the left of `clientX`.
 */
export function inspectorWidthFromPointer(viewportWidth: number, clientX: number): number {
  return clampInspectorWidth(viewportWidth - clientX, viewportWidth);
}

/**
 * Keyboard resize for a right-docked panel. ArrowLeft moves the handle left (wider);
 * ArrowRight shrinks it. Home/End jump to the clamps.
 */
export function inspectorWidthFromKey(
  current: number,
  key: string,
  viewportWidth = Number.POSITIVE_INFINITY,
): number | null {
  if (key === 'ArrowLeft') return clampInspectorWidth(current + INSPECTOR_WIDTH_STEP, viewportWidth);
  if (key === 'ArrowRight') return clampInspectorWidth(current - INSPECTOR_WIDTH_STEP, viewportWidth);
  if (key === 'Home') return clampInspectorWidth(MIN_INSPECTOR_WIDTH, viewportWidth);
  if (key === 'End') return clampInspectorWidth(MAX_INSPECTOR_WIDTH, viewportWidth);
  return null;
}
